<template>
  <OurPage data-cy="users-page" :title="$t('title.customers._')" gray class="users-page">
    <CustomersList
      :items="customers"
      :headers="customersListHeaders"
      :pagination-data="paginationData"
      :is-loading="isLoading"
      @pageChange="getData"
      @onClickRow="onClickRow"
    />

    <FilterModal :show.sync="isShownFilterModal" />

    <template #header-right>
      <div class="buttons">
        <OurButton
          variant="thirdary"
          filled
          :text="$t('button.filters')"
          @click="onClickFilterButton"
        >
          <OurSvgIcon v-if="isMobileDevice" :src="icons.FilterAltFill" />
        </OurButton>

        <TableColumnsSettings
          v-if="!isMobileDevice && isTableColumnsReady"
          :headers="tableHeaders"
          :table-key="tablePreferenceKey"
          data-cy="customers-list-columns"
        />

        <OurLink v-if="getPermission['customer.create']" :route="addRoute">
          <OurButton :text="$t('button.add')" />
        </OurLink>
      </div>
    </template>
  </OurPage>
</template>

<script>
import { mapGetters, mapMutations, mapState } from "vuex";
import apolloMixin from "@/mixins/apollo.mixin";
import { accessGroupsQuery, customersQuery, allCustomersQuery } from "./gql/queries";
import CustomersList from "@/views/settings/customers/index/_components/CustomersList.vue";
import FilterModal from "@/views/settings/customers/filter/Modal.vue";
import permissionsMixin from "@/mixins/permissions.mixin";
import tableColumnsMixin from "@/mixins/tableColumns.mixin";
import TableColumnsSettings from "@/components/TableColumnsSettings.vue";
import { buildCustomersListTableHeaders } from "@/views/settings/customers/index/config/customersListTableHeaders";

export default {
  name: "Index",

  components: { FilterModal, CustomersList, TableColumnsSettings },

  mixins: [apolloMixin, permissionsMixin, tableColumnsMixin],

  data() {
    return {
      customers: [],
      paginationData: {},
      isShownFilterModal: false,
      isLoading: false,
      tableColumnsId: "customer-list",
    };
  },

  computed: {
    ...mapState("customers", ["filtersForm"]),
    ...mapGetters("breakpoint", ["isMobileDevice"]),
    ...mapGetters("user", ["userProfile", "isUserDealer", "isUserDealerAdmin", "getPermission"]),
    ...mapGetters("customers", ["getStatus", "getFiltersOptions"]),

    addRoute() {
      return { name: "CustomersAdd" };
    },

    icons: () => ({
      FilterAltFill: require("@material-symbols/svg-400/outlined/filter_alt-fill.svg"),
    }),

    tableHeaders() {
      return buildCustomersListTableHeaders({
        t: (key) => this.$t(key),
      });
    },

    customersListHeaders() {
      if (!this.isTableColumnsReady) {
        return null;
      }

      return this.visibleTableHeaders;
    },
  },

  watch: {
    filtersForm: {
      handler: "onChangeFiltersForm",
      deep: true,
    },
  },

  async created() {
    await this.getAllCustomers();
    await this.getAccessGroupsAndManagers();
  },

  methods: {
    ...mapMutations("dealerCompany", ["SET_ACCESS_GROUPS"]),
    ...mapMutations("dealerUsers", ["SET_CUSTOMERS"]),

    async getAccessGroupsAndManagers() {
      const { accessGroups } = await this.$get(accessGroupsQuery);

      if (accessGroups) {
        this.SET_ACCESS_GROUPS(accessGroups);
      }
    },

    async getAllCustomers() {
      const { allCustomer } = await this.$get(allCustomersQuery);

      this.SET_CUSTOMERS(allCustomer);
    },

    async getData(page = parseInt(this.$route.query.page) ?? 1) {
      this.isLoading = true;

      try {
        const filtersOptions = this.getFiltersOptions();

        const { customers } = await this.$get(customersQuery, { page, filtersOptions });

        if (customers) {
          this.customers = customers.data.map((customer) => {
            const { id, name, isActive, companies, accessGroup, dealers, bonusProgramLevel } =
              customer;

            return {
              id,
              name,
              companies,
              accessGroup: accessGroup?.name,
              dealers,
              bonusProgramLevel: bonusProgramLevel?.name,
              status: this.getStatus(isActive ? "activated" : "deactivated"),
              page: "CustomersEdit",
            };
          });

          this.paginationData = customers.paginatorInfo;
        }
      } finally {
        this.isLoading = false;
      }
    },

    onClickFilterButton() {
      this.isShownFilterModal = true;
    },

    onChangeFiltersForm() {
      this.getData();
    },

    onClickRow(id) {
      this.$router.push({ name: "CustomersEdit", params: { id } });
    },
  },
};
</script>

<style lang="postcss" scoped>
.users-page {
  .buttons {
    @apply flex items-center justify-between gap-4 sm:justify-start;
  }
}
</style>
